import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Copy, FileText, MagnifyingGlass, Download } from "@phosphor-icons/react";

const CATEGORIES = ["general", "contract", "completion_certificate", "letter", "notice", "agreement"];

export default function DocumentTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", category: "general", content: "" });
  const [saving, setSaving] = useState(false);
  const [showFill, setShowFill] = useState(false);
  const [fillTemplate, setFillTemplate] = useState(null);
  const [fillContext, setFillContext] = useState({});
  const [filledContent, setFilledContent] = useState("");
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get("/document-templates").then(({ data }) => setTemplates(data.templates || [])),
      API.get("/document-templates/fields").then(({ data }) => setFields(data.fields || [])),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Template name required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/document-templates/${editingId}`, form);
        toast.success("Template updated");
      } else {
        await API.post("/document-templates", form);
        toast.success("Template created");
      }
      setShowEditor(false); setEditingId(null);
      setForm({ name: "", category: "general", content: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await API.delete(`/document-templates/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const handleFill = async () => {
    try {
      const { data } = await API.post(`/document-templates/${fillTemplate.id}/fill`, { context: fillContext });
      setFilledContent(data.filled_content);
      toast.success("Template filled");
    } catch (e) { toast.error("Failed to fill"); }
  };

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.category.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="document-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Document Templates</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{templates.length} template{templates.length !== 1 ? "s" : ""} — auto-fill with merge fields</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search..." className="pl-8 h-8 w-44 text-xs" value={search} onChange={e => setSearch(e.target.value)} data-testid="dt-search" />
          </div>
          <Button size="sm" onClick={() => { setEditingId(null); setForm({ name: "", category: "general", content: "" }); setShowEditor(true); }} data-testid="dt-create-btn">
            <Plus size={14} className="mr-1" /> New Template
          </Button>
        </div>
      </div>

      {/* Merge Fields Reference */}
      <details className="mb-4 border border-border rounded-lg overflow-hidden" data-testid="dt-fields-ref">
        <summary className="px-4 py-2.5 bg-muted/30 cursor-pointer text-xs font-semibold hover:bg-muted/50 transition-colors">Available Merge Fields ({fields.length})</summary>
        <div className="px-4 py-3 flex flex-wrap gap-2">
          {fields.map(f => (
            <button key={f.key} onClick={() => navigator.clipboard.writeText(f.key).then(() => toast.success(`Copied ${f.key}`))} className="text-[10px] font-mono px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer" title={`Click to copy: ${f.key}`}>
              {f.key} <span className="text-muted-foreground ml-1">({f.label})</span>
            </button>
          ))}
        </div>
      </details>

      {/* Templates Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <FileText size={36} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No templates yet. Create your first one.</p>
          <Button size="sm" onClick={() => setShowEditor(true)}><Plus size={14} className="mr-1" /> Create Template</Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="dt-grid">
          {filtered.map(t => (
            <div key={t.id} className="border border-border rounded-lg p-4 bg-card card-lift" data-testid={`dt-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold">{t.name}</h3>
                  <Badge variant="outline" className="text-[9px] mt-0.5">{t.category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingId(t.id); setForm({ name: t.name, category: t.category, content: t.content }); setShowEditor(true); }} data-testid={`dt-edit-${t.id}`}><PencilSimple size={12} /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(t.id)} data-testid={`dt-delete-${t.id}`}><Trash size={12} /></Button>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground line-clamp-3 mb-3">{t.content?.substring(0, 150)}...</p>
              <Button variant="outline" size="sm" className="text-[10px] w-full" onClick={() => { setFillTemplate(t); setFillContext({}); setFilledContent(""); setShowFill(true); }} data-testid={`dt-fill-${t.id}`}>
                <Copy size={12} className="mr-1" /> Auto-Fill & Generate
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dt-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editingId ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription>Use merge fields like {"{{client_name}}"} in your content. They will be replaced with real data when filled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Template Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" data-testid="dt-form-name" /></div>
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1" data-testid="dt-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Content</Label>
              <Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="mt-1 h-64 text-xs font-mono" placeholder="Dear {{client_name}},&#10;&#10;Re: {{project_name}} — {{project_reference}}&#10;&#10;..." data-testid="dt-form-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="dt-form-save">{saving ? "Saving..." : editingId ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fill Dialog */}
      <Dialog open={showFill} onOpenChange={setShowFill}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dt-fill-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Fill: {fillTemplate?.name}</DialogTitle>
            <DialogDescription>Enter values for the merge fields below. Leave blank to skip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {fields.map(f => {
                const key = f.key.replace(/\{|\}/g, "");
                return (
                  <div key={key}>
                    <Label className="text-[10px]">{f.label}</Label>
                    <Input value={fillContext[key] || ""} onChange={e => setFillContext(p => ({ ...p, [key]: e.target.value }))} className="mt-0.5 h-8 text-xs" data-testid={`dt-fill-${key}`} />
                  </div>
                );
              })}
            </div>
            <Button onClick={handleFill} className="w-full" data-testid="dt-fill-generate"><Download size={14} className="mr-1" /> Generate Document</Button>
            {filledContent && (
              <div className="border border-border rounded-lg overflow-hidden" data-testid="dt-filled-output">
                <div className="px-3 py-1.5 bg-muted/40 border-b border-border flex justify-between items-center">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Generated Output</span>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => { navigator.clipboard.writeText(filledContent); toast.success("Copied to clipboard"); }}>
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                </div>
                <pre className="p-4 text-xs whitespace-pre-wrap max-h-80 overflow-y-auto bg-card">{filledContent}</pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
